import { Inject, Injectable, Optional } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { ConcurrentUsageService } from '../../../services/concurrent-usage/concurrent-usage.service';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    deleteUserStoreValue,
    deleteUserStoreValueFailed,
    deleteUserStoreValueSuccess,
    loadUserStore,
    retryPendingUserStoreDeletes,
    setUserStoreValue,
    setUserStoreValueFailed,
    setUserStoreValueSuccess,
    userStoreEntryMirrored,
    userStoreEntryUnmirrored,
    userStoreFailed,
    userStoreLoaded,
    userStoreLoadedFromStorage,
} from '../actions/user-store.actions';
import { parseHydratedUserStoreState, userStoreEntriesToState } from '../user-store.converters';
import { USER_STORE_FEATURE_KEY, USER_STORE_SYNCED_KEYS, USER_STORE_UNMIRRORED_KEYS } from '../user-store.state';
import { selectUserStoreLoaded, selectUserStorePendingDeletes } from '../selectors/user-store.selector';

/**
 * Owns the user-store load/write-through flow and the persistence mirror. `ConcurrentUsageService`
 * is the only collaborator that knows HTTP — this class translates NgRx actions into calls on it
 * and back. A user-store value is not treated specially there: `setUserStoreValue$`/
 * `deleteUserStoreValue$` go through the exact same `writeDataForKey`/`deleteDataForKey` (with
 * `uploadImmediately: true`) any other concurrent-usage entry uses, so every value this class
 * writes is also cached locally, encrypted, through `ConcurrentUsageDbService` before the upload
 * is attempted. `ConcurrentUsageService.getUserStoreEntries` (used by `loadUserStore$`) unwraps
 * that same wire format back to the raw value on the way out. Because of that local copy, the
 * device check-out (`ConcurrentUsageService.uploadAll`) sends every `USER_STORE_SYNCED_KEYS` value
 * again, so a value whose immediate upload never landed still reaches Azure before the device lock
 * is released.
 *
 * Every write (`setUserStoreValue$`/`deleteUserStoreValue$`) is split into two independent
 * effects: one that mirrors the change into the slice (skipped for a key in
 * `USER_STORE_UNMIRRORED_KEYS`) and one that always talks to Azure regardless of exclusion. That
 * split is what lets an excluded key's write reach Azure while never touching the in-memory
 * mirror or its persisted cache — the reducer only ever sees the already-filtered
 * `userStoreEntryMirrored`/`userStoreEntryUnmirrored` actions, never the raw
 * `setUserStoreValue`/`deleteUserStoreValue` a caller dispatched.
 *
 * Offline-first, genuinely: `userStoreEntryMirrored`/`userStoreEntryUnmirrored` persist the slice
 * immediately, before Azure has answered (see `persistUserStoreOnMirror$`/
 * `persistUserStoreOnUnmirror$`), so a change made while offline survives an app restart. The
 * upsert side of the write-through then relies on `ENDPOINTS.AzUserStore` being registered with
 * `PaceOfflineModule` (see the app's root module) to queue and replay the PUT. The delete side
 * cannot use that same queue — `UserStoreApiService.deleteEntry` sends `ONLINE_HEADER` so it always
 * bypasses it, because the queue replays everything as a POST and a queued DELETE would arrive at
 * the backend bodiless and be rejected forever — so a mirrored key's delete instead goes into
 * `pendingDeletes` and is retried by `triggerPendingUserStoreDeleteRetry$`/
 * `retryPendingUserStoreDeletesRemote$` below.
 *
 * The straightforward version of the delete/recreate race — delete a key while offline, then set
 * it again before the retry fires — is already closed: `mirrorUserStoreEntryReducer` clears any
 * `pendingDeletes` entry for the same key the moment it mirrors a value, in the same state update,
 * so there is no window where the state holds both the fresh value and a now-obsolete pending
 * delete for it. `retryPendingUserStoreDeletesRemote$` reads `pendingDeletes` fresh each time it
 * runs, so a key cleared this way is simply never sent.
 *
 * Residual ordering caveat: what remains is a race at the network layer, not in this slice's state.
 * `triggerPendingUserStoreDeleteRetry$` waits for `OfflineService.isOnline$` and then, once, for
 * `OfflineService.isSyncing$` to go false, so a queued upsert for the same key gets its chance to
 * be *sent* before the retried DELETE goes out. That wait is a one-time snapshot check, not a lock:
 * if the retried DELETE and a fresh queued PUT for the same key are both in flight around the same
 * moment (e.g. the user recreates the key after the pending-delete retry has already read
 * `pendingDeletes` and started its request, but before that request lands), whichever the backend
 * processes last wins, regardless of which the user actually intended to be current. Closing that
 * fully would need the backend itself to serialize per-key writes/deletes (e.g. a version check),
 * which is out of scope here.
 */
@Injectable()
export class UserStoreEffects {
    private readonly unmirroredKeys: string[];
    /** The keys `loadUserStore$` requests: the synced keys that are mirrored. */
    private readonly loadedKeys: readonly string[];

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly concurrentUsage: ConcurrentUsageService,
        private readonly offlineService: OfflineService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Optional() @Inject(USER_STORE_UNMIRRORED_KEYS) unmirroredKeys: string[] | null,
    ) {
        this.unmirroredKeys = unmirroredKeys ?? [];
        this.loadedKeys = USER_STORE_SYNCED_KEYS.filter((key) => !this.unmirroredKeys.includes(key));
    }

    /**
     * Fetch the slice's own keys (`USER_STORE_SYNCED_KEYS`) from the backend and populate the
     * mirror. An excluded key is not even requested; `userStoreEntriesToState` drops it again as a
     * second line of defence. The user store also holds the device-handover entries of
     * `ConcurrentUsageService`, which is why the slice never reads the whole namespace.
     */
    public loadUserStore$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadUserStore),
            switchMap(() =>
                this.concurrentUsage.getUserStoreEntries(this.loadedKeys).pipe(
                    map((entries) =>
                        userStoreLoaded({ entries: userStoreEntriesToState(entries, this.unmirroredKeys) }),
                    ),
                    catchError((error) => {
                        this.log.error('UserStoreEffects: loadUserStore$: Failed to fetch user-store entries:', error);
                        return of(userStoreFailed());
                    }),
                ),
            ),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateUserStoreOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadUserStore),
            concatLatestFrom(() => this.store.select(selectUserStoreLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: USER_STORE_FEATURE_KEY })),
        ),
    );

    /**
     * Bridge the generic hydrate result into the slice, unless the backend already won.
     * `parseHydratedUserStoreState` falls back to the empty default for `undefined` or anything
     * not shaped like the expected `{ entries, pendingDeletes }` object, so a missing or malformed
     * cache entry restores the initial state instead of throwing.
     */
    public applyHydratedUserStore$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === USER_STORE_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectUserStoreLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) => userStoreLoadedFromStorage(parseHydratedUserStoreState(data))),
        ),
    );

    /** Cache the slice whenever the bulk backend load populates it. */
    public persistUserStoreOnLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userStoreLoaded),
            map(() => persistSlice({ featureKey: USER_STORE_FEATURE_KEY })),
        ),
    );

    /**
     * Mirrors a `setUserStoreValue` into the slice, unless its key is excluded. Kept separate from
     * `putUserStoreValue$` so an excluded key's PUT still reaches Azure below without ever
     * touching the in-memory mirror.
     */
    public mirrorSetUserStoreValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setUserStoreValue),
            filter(({ key }) => !this.unmirroredKeys.includes(key)),
            map(({ key, value }) => userStoreEntryMirrored({ key, value })),
        ),
    );

    /**
     * Persists the slice the moment a value is mirrored — NOT once Azure confirms it (see
     * `setUserStoreValueSuccess`'s doc comment). This is what makes an offline `setUserStoreValue`
     * survive an app restart: the mirror is the source of truth for what the user asked to store,
     * and IndexedDB now follows it immediately rather than waiting on a round trip that may be
     * queued for a long time.
     */
    public persistUserStoreOnMirror$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userStoreEntryMirrored),
            map(() => persistSlice({ featureKey: USER_STORE_FEATURE_KEY })),
        ),
    );

    /**
     * Writes the full value to the local encrypted cache and PUTs it to Azure for every
     * `setUserStoreValue`, mirrored or not — via `ConcurrentUsageService.writeDataForKey`'s
     * `uploadImmediately` option, the same local-store-then-upload primitive any other
     * concurrent-usage entry uses, just without waiting for a later `uploadAll`. The value is
     * JSON-stringified for that call since `writeDataForKey` (and the wire format `writeRemote`
     * uploads it with) is string-typed throughout; `getAllUserStoreEntries` reverses this on load.
     *
     * PUT, not PATCH: `OfflineInterceptorService.CACHEABLE_REQUEST_METHODS` only queues
     * `post`/`put`/`delete`, so an offline PATCH would throw instead of being queued while the
     * device is offline. The slice always holds the complete mirrored value, so it can always PUT
     * the full new value and rely on the offline queue to replay it once connectivity returns —
     * `ENDPOINTS.AzUserStore` must be (and is) registered with `PaceOfflineModule` for that queue to
     * pick this URL up at all. `UserStoreApiService.patchEntry` stays available for callers with
     * non-mirrored keys that accept an offline write failing outright.
     *
     * A failed PUT is reported via `setUserStoreValueFailed` but the mirror is deliberately NOT
     * rolled back, and stays persisted: the slice is the source of truth for what the user asked to
     * store, and silently reverting (or un-persisting) it would hide the edit rather than surface
     * the failure. The offline queue (for a genuine connectivity failure) will retry the PUT until
     * it succeeds; a hard backend rejection is left for the caller watching
     * `setUserStoreValueFailed` to handle. The local encrypted cache write, unlike the PUT, cannot
     * fail offline — it always lands before the upload is attempted.
     *
     * `OfflineService.sendOfflineData` is still fired here (mirroring `EscalationEffects.saveEscalationStatus$`),
     * to flush the queued PUT immediately rather than leaving it for the next unrelated sync — but only
     * *after* `setUserStoreValueSuccess` has been mapped, via a `tap` that subscribes to it independently
     * instead of merging it into the effect's output. `writeDataForKey` completing already means the
     * offline interceptor queued (or sent) the PUT, which is success from this slice's point of view
     * regardless of connectivity; `sendOfflineData` has nothing to send to while offline and can reject
     * (e.g. `OFFLINE_PLUGIN_SYNC_ERROR`) or simply hang waiting for connectivity, so chaining it before the
     * `map` — as this effect used to do — left `setUserStoreValueSuccess` stuck behind a promise that
     * offline may never settle. Its rejection is caught and logged on its own subscription, not routed
     * through the write's `catchError`, so a `sendOfflineData` failure can never turn an already-successful
     * write into `setUserStoreValueFailed`.
     */
    public putUserStoreValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setUserStoreValue),
            mergeMap(({ key, value }) =>
                this.concurrentUsage.writeDataForKey(key, JSON.stringify(value), Date.now(), true).pipe(
                    map(() => setUserStoreValueSuccess({ key })),
                    tap(() => {
                        this.offlineService.sendOfflineData().subscribe({
                            error: (error) =>
                                this.log.error(
                                    `UserStoreEffects: putUserStoreValue$: sendOfflineData failed after saving '${key}':`,
                                    error,
                                ),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `UserStoreEffects: putUserStoreValue$: Failed to save '${key}' to Azure:`,
                            error,
                        );
                        return of(setUserStoreValueFailed({ key, error: String(error) }));
                    }),
                ),
            ),
        ),
    );

    /** Mirrors a `deleteUserStoreValue` out of the slice, unless its key is excluded — mirrors `mirrorSetUserStoreValue$`. */
    public mirrorDeleteUserStoreValue$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserStoreValue),
            filter(({ key }) => !this.unmirroredKeys.includes(key)),
            map(({ key }) => userStoreEntryUnmirrored({ key })),
        ),
    );

    /**
     * Persists the slice the moment a value is unmirrored — mirrors `persistUserStoreOnMirror$`.
     * Both the entry's removal and its new `pendingDeletes` entry (see
     * `unmirrorUserStoreEntryReducer`) need to survive a restart before Azure has answered, since
     * the delete itself bypasses the offline request queue (see `UserStoreApiService.deleteEntry`).
     */
    public persistUserStoreOnUnmirror$ = createEffect(() =>
        this.actions$.pipe(
            ofType(userStoreEntryUnmirrored),
            map(() => persistSlice({ featureKey: USER_STORE_FEATURE_KEY })),
        ),
    );

    /**
     * DELETEs the entry from Azure for every `deleteUserStoreValue`, mirrored or not.
     *
     * `OfflineService.sendOfflineData` is fired here too (see `putUserStoreValue$`'s doc comment for
     * why it runs via `tap`, after `deleteUserStoreValueSuccess` is mapped, on its own subscription
     * rather than chained before the `map`), so a delete also flushes whatever else is sitting in the
     * offline request queue — the delete itself still bypasses that queue (see this class's doc
     * comment), but there is no reason to leave an unrelated queued write stranded until the next
     * incidental sync just because the user's last action happened to be a delete, nor to leave
     * `deleteUserStoreValueSuccess` stuck behind a `sendOfflineData` that has nothing to send to while
     * offline.
     */
    public deleteUserStoreValueRemote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserStoreValue),
            mergeMap(({ key }) =>
                this.concurrentUsage.deleteDataForKey(key, true).pipe(
                    map(() => deleteUserStoreValueSuccess({ key })),
                    tap(() => {
                        this.offlineService.sendOfflineData().subscribe({
                            error: (error) =>
                                this.log.error(
                                    `UserStoreEffects: deleteUserStoreValueRemote$: sendOfflineData failed after deleting '${key}':`,
                                    error,
                                ),
                        });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `UserStoreEffects: deleteUserStoreValueRemote$: Failed to delete '${key}' from Azure:`,
                            error,
                        );
                        return of(deleteUserStoreValueFailed({ key, error: String(error) }));
                    }),
                ),
            ),
        ),
    );

    /**
     * Persists the slice once a delete is confirmed (including a 404, treated as success — see
     * `UserStoreApiService.deleteEntry`), so the `pendingDeletes` entry that
     * `clearPendingUserStoreDeleteReducer` just cleared does not linger in storage until some
     * unrelated mirror/unmirror action happens to write it out. This is additive to
     * `persistUserStoreOnMirror$`/`persistUserStoreOnUnmirror$`, not a replacement: those persist
     * the local mirror itself the moment it changes, independent of Azure's answer; this one persists
     * the bookkeeping change that only exists once Azure (or a 404) confirms it.
     */
    public persistUserStoreOnDeleteConfirmed$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserStoreValueSuccess),
            map(() => persistSlice({ featureKey: USER_STORE_FEATURE_KEY })),
        ),
    );

    /**
     * Fires the pending-delete retry whenever the device is back online AND the offline request
     * queue has drained. `OfflineService.isOnline$` replays its current value to a new subscriber
     * (see `defaultOnlineDetector`), so this also fires once on startup if the app happens to
     * already be online with leftover `pendingDeletes` from a previous session. Waiting for
     * `isSyncing$` to go false first gives a queued upsert for the same key its chance to land
     * before the retried DELETE is sent — see this class's doc comment for the ordering caveat that
     * waiting cannot fully close, since it is a one-time snapshot check rather than a lock.
     */
    public triggerPendingUserStoreDeleteRetry$ = createEffect(() =>
        this.offlineService.isOnline$.pipe(
            filter((online) => online),
            switchMap(() =>
                this.offlineService.isSyncing$.pipe(
                    filter((syncing) => !syncing),
                    take(1),
                ),
            ),
            map(() => retryPendingUserStoreDeletes()),
        ),
    );

    /**
     * Resends the DELETE for every key currently in `pendingDeletes` once
     * `retryPendingUserStoreDeletes` fires. Reuses `deleteUserStoreValueSuccess`/
     * `deleteUserStoreValueFailed` — from the reducer's point of view a retried delete looks exactly
     * like the original one, and `clearPendingUserStoreDeleteReducer` clears the key on success
     * either way (a 404 counts as success, see `UserStoreApiService.deleteEntry`). A failure here
     * leaves the key in `pendingDeletes`, so the next time the device goes online it is retried
     * again.
     */
    public retryPendingUserStoreDeletesRemote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(retryPendingUserStoreDeletes),
            concatLatestFrom(() => this.store.select(selectUserStorePendingDeletes)),
            switchMap(([, pendingDeletes]) => from(pendingDeletes)),
            mergeMap((key) =>
                this.concurrentUsage.deleteDataForKey(key, true).pipe(
                    map(() => deleteUserStoreValueSuccess({ key })),
                    catchError((error) => {
                        this.log.error(
                            `UserStoreEffects: retryPendingUserStoreDeletesRemote$: Failed to retry delete of '${key}' from Azure:`,
                            error,
                        );
                        return of(deleteUserStoreValueFailed({ key, error: String(error) }));
                    }),
                ),
            ),
        ),
    );
}
